import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  GripVertical, Plus, Trash2, ChevronDown, ChevronUp, X, Loader2, Type, List as ListIcon,
  AlignLeft, Upload, Calendar, Hash, Flag, Timer, PenTool, CheckSquare, Edit3, Save, Layers,
  FileText, Zap
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const FIELD_TYPES = [
  { value: "text", label: "Texte court", icon: Type },
  { value: "textarea", label: "Texte long", icon: AlignLeft },
  { value: "list", label: "Liste déroulante", icon: ListIcon },
  { value: "number", label: "Nombre", icon: Hash },
  { value: "counter", label: "Compteur", icon: Hash },
  { value: "upload", label: "Fichier / Photo", icon: Upload },
  { value: "priority", label: "Priorité", icon: Flag },
  { value: "deadline", label: "Délai / Deadline", icon: Timer },
  { value: "date", label: "Date", icon: Calendar },
  { value: "signature", label: "Signature", icon: PenTool },
  { value: "checkbox", label: "Case à cocher", icon: CheckSquare },
];

const genId = () => `f_${Math.random().toString(36).slice(2, 10)}`;

export default function AdminFormBuilder() {
  const [schemas, setSchemas] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragField, setDragField] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [formOptions, setFormOptions] = useState({});
  const [modal, setModal] = useState(null);
  const [newSchema, setNewSchema] = useState({ page_key: "", title: "" });

  const loadSchemas = async () => {
    setLoading(true);
    try {
      const [{ data }, opts] = await Promise.all([
        api.get("/admin/form-schemas"),
        api.get("/admin/form-options"),
      ]);
      setSchemas(data.items || []);
      setFormOptions(opts.data.by_form || {});
      if (data.items?.length && !current) setCurrent(data.items[0]);
    } catch { toast.error("Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { loadSchemas(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/form-schemas", current);
      toast.success("Formulaire enregistré");
      setCurrent(data); setDirty(false);
      loadSchemas();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const deleteSchema = async () => {
    if (!current || !confirm(`Supprimer le formulaire « ${current.title} » ?`)) return;
    try {
      await api.delete(`/admin/form-schemas/${current.id}`);
      toast.success("Supprimé");
      setCurrent(null); loadSchemas();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createSchema = async (e) => {
    e.preventDefault();
    const key = newSchema.page_key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!key || !newSchema.title.trim()) return;
    const schema = {
      page_key: key, title: newSchema.title.trim(), description: "",
      sections: [{ id: genId(), title: "Section principale", fields: [] }], active: true,
    };
    try {
      const { data } = await api.post("/admin/form-schemas", schema);
      toast.success("Formulaire créé");
      setNewSchema({ page_key: "", title: "" }); setModal(null);
      setCurrent(data); loadSchemas();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ── mutation helpers ────────────────────────────────────────────────
  const mutSections = (mut) => { setCurrent(c => ({ ...c, sections: mut(c.sections) })); setDirty(true); };
  const addSection = () => mutSections(ss => [...ss, { id: genId(), title: `Section ${ss.length + 1}`, description: "", fields: [] }]);
  const updateSection = (sid, patch) => mutSections(ss => ss.map(s => s.id === sid ? { ...s, ...patch } : s));
  const removeSection = (sid) => { if (!confirm("Supprimer cette section et tous ses champs ?")) return; mutSections(ss => ss.filter(s => s.id !== sid)); };
  const moveSection = (sid, dir) => mutSections(ss => {
    const i = ss.findIndex(s => s.id === sid); const j = i + dir;
    if (i < 0 || j < 0 || j >= ss.length) return ss;
    const n = [...ss]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const addField = (sid, type) => mutSections(ss => ss.map(s => s.id === sid
    ? { ...s, fields: [...s.fields, { id: genId(), type, label: "Nouveau champ", required: false }] } : s));
  const updateField = (sid, fid, patch) => mutSections(ss => ss.map(s => s.id === sid
    ? { ...s, fields: s.fields.map(f => f.id === fid ? { ...f, ...patch } : f) } : s));
  const removeField = (sid, fid) => mutSections(ss => ss.map(s => s.id === sid
    ? { ...s, fields: s.fields.filter(f => f.id !== fid) } : s));
  const moveField = (sid, fid, dir) => mutSections(ss => ss.map(s => {
    if (s.id !== sid) return s;
    const i = s.fields.findIndex(f => f.id === fid); const j = i + dir;
    if (i < 0 || j < 0 || j >= s.fields.length) return s;
    const n = [...s.fields]; [n[i], n[j]] = [n[j], n[i]]; return { ...s, fields: n };
  }));

  // ── drag-and-drop ───────────────────────────────────────────────────
  const onFieldDragStart = (sid, fid) => setDragField({ sid, fid });
  const onFieldDrop = (targetSid, targetFid) => {
    if (!dragField) return;
    const { sid, fid } = dragField;
    if (sid === targetSid && fid === targetFid) return;
    mutSections(ss => {
      const clone = ss.map(s => ({ ...s, fields: [...s.fields] }));
      const src = clone.find(s => s.id === sid); if (!src) return ss;
      const idx = src.fields.findIndex(f => f.id === fid); if (idx < 0) return ss;
      const [field] = src.fields.splice(idx, 1);
      const dst = clone.find(s => s.id === targetSid); if (!dst) return ss;
      const dstIdx = targetFid ? dst.fields.findIndex(f => f.id === targetFid) : dst.fields.length;
      dst.fields.splice(dstIdx >= 0 ? dstIdx : dst.fields.length, 0, field);
      return clone;
    });
    setDragField(null);
  };
  const onSectionDrop = (targetSid) => {
    if (!dragSection || dragSection === targetSid) return;
    mutSections(ss => {
      const src = ss.findIndex(s => s.id === dragSection);
      const dst = ss.findIndex(s => s.id === targetSid);
      if (src < 0 || dst < 0) return ss;
      const n = [...ss]; const [it] = n.splice(src, 1); n.splice(dst, 0, it); return n;
    });
    setDragSection(null);
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline"/></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="admin-form-builder">
      {/* LEFT — list of schemas */}
      <div className="lg:col-span-1 space-y-2">
        <div className="flex items-center justify-between px-1">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Formulaires ({schemas.length})</div>
          <button onClick={() => setModal("new")} data-testid="fb-new-schema"
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
            <Plus className="w-3 h-3"/> Nouveau
          </button>
        </div>
        {schemas.map(s => {
          const on = current?.id === s.id;
          return (
            <button key={s.id} onClick={() => { setCurrent(s); setDirty(false); }} data-testid={`fb-schema-${s.page_key}`}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${on ? "bg-white/10" : "bg-blue-50 text-blue-700"} grid place-items-center shrink-0`}>
                  <FileText className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.title}</div>
                  <div className={`text-[10px] font-mono ${on ? "text-slate-300" : "text-slate-500"}`}>page: {s.page_key}</div>
                </div>
              </div>
              <div className={`text-[10px] mt-1 ${on ? "text-slate-300" : "text-slate-400"}`}>
                {s.sections?.length || 0} section{(s.sections?.length || 0) > 1 ? "s" : ""} · {(s.sections || []).reduce((a, sec) => a + (sec.fields?.length || 0), 0)} champs
              </div>
            </button>
          );
        })}
        {schemas.length === 0 && (
          <div className="text-center py-6 text-xs text-slate-400 italic">Aucun formulaire encore.</div>
        )}
      </div>

      {/* RIGHT — editor */}
      <div className="lg:col-span-3 space-y-4">
        {!current ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-16 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto"/>
            <div className="mt-3 text-sm text-slate-500">Sélectionnez un formulaire ou créez-en un nouveau.</div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Titre du formulaire</label>
                  <input value={current.title} onChange={e => { setCurrent({...current, title: e.target.value}); setDirty(true); }}
                         data-testid="fb-title"
                         className="w-full mt-0.5 text-xl font-display font-bold text-slate-900 border-b border-slate-200 focus:border-blue-500 outline-none pb-1 bg-transparent"/>
                </div>
                <div className="flex items-center gap-2">
                  {dirty && <span className="text-[10px] font-bold uppercase text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">Non enregistré</span>}
                  <button onClick={deleteSchema} data-testid="fb-delete"
                          className="px-3 py-2 rounded-xl bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-bold">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={save} disabled={saving || !dirty} data-testid="fb-save"
                          className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Enregistrer
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Page cible (clé)</label>
                  <input value={current.page_key} onChange={e => { setCurrent({...current, page_key: e.target.value}); setDirty(true); }}
                         className="w-full mt-0.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono bg-slate-50"/>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Description</label>
                  <input value={current.description || ""} onChange={e => { setCurrent({...current, description: e.target.value}); setDirty(true); }}
                         className="w-full mt-0.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
              </div>
            </div>

            {/* Sections */}
            {(current.sections || []).map((sec, si) => (
              <div key={sec.id} draggable onDragStart={() => setDragSection(sec.id)} onDragOver={e => e.preventDefault()}
                   onDrop={() => onSectionDrop(sec.id)} data-testid={`fb-section-${sec.id}`}
                   className={`bg-white border-2 rounded-2xl p-5 transition-all ${dragSection === sec.id ? "border-blue-400 shadow-lg" : "border-slate-200"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" title="Glisser pour réordonner">
                    <GripVertical className="w-4 h-4"/>
                  </div>
                  <input value={sec.title} onChange={e => updateSection(sec.id, { title: e.target.value })}
                         className="flex-1 text-lg font-display font-bold text-slate-900 border-b border-transparent hover:border-slate-200 focus:border-blue-500 outline-none pb-0.5 bg-transparent"
                         placeholder="Titre de section"/>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveSection(sec.id, -1)} disabled={si === 0}
                            className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="w-4 h-4"/></button>
                    <button onClick={() => moveSection(sec.id, 1)} disabled={si === (current.sections.length - 1)}
                            className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="w-4 h-4"/></button>
                    <button onClick={() => removeSection(sec.id)} data-testid={`fb-remove-section-${sec.id}`}
                            className="w-7 h-7 grid place-items-center rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4"/></button>
                  </div>
                </div>

                {/* Fields */}
                <div className="space-y-2">
                  {(sec.fields || []).map((f, fi) => {
                    const meta = FIELD_TYPES.find(t => t.value === f.type) || FIELD_TYPES[0];
                    const Icon = meta.icon;
                    return (
                      <div key={f.id} draggable onDragStart={() => onFieldDragStart(sec.id, f.id)}
                           onDragOver={e => e.preventDefault()} onDrop={() => onFieldDrop(sec.id, f.id)}
                           data-testid={`fb-field-${f.id}`}
                           className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all ${dragField?.fid === f.id ? "border-blue-400 bg-blue-50" : "border-slate-100 hover:border-slate-300 bg-slate-50/50"}`}>
                        <div className="cursor-grab active:cursor-grabbing text-slate-400"><GripVertical className="w-4 h-4"/></div>
                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 grid place-items-center text-slate-600 shrink-0">
                          <Icon className="w-4 h-4"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <input value={f.label} onChange={e => updateField(sec.id, f.id, { label: e.target.value })}
                                   className="flex-1 font-semibold text-sm text-slate-800 bg-transparent outline-none border-b border-transparent focus:border-blue-500"/>
                            {f.required && <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full">Obligatoire</span>}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            <span className="font-semibold">{meta.label}</span>
                            {f.form_options_key && <> · liste : <code className="font-mono">{f.form_options_key}</code></>}
                            {f.placeholder && <> · « {f.placeholder} »</>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => moveField(sec.id, f.id, -1)} disabled={fi === 0}
                                  className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5"/></button>
                          <button onClick={() => moveField(sec.id, f.id, 1)} disabled={fi === (sec.fields.length - 1)}
                                  className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5"/></button>
                          <button onClick={() => setEditingField({ sid: sec.id, field: f })} data-testid={`fb-edit-field-${f.id}`}
                                  className="w-7 h-7 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100"><Edit3 className="w-3.5 h-3.5"/></button>
                          <button onClick={() => removeField(sec.id, f.id)}
                                  className="w-7 h-7 grid place-items-center rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5"/></button>
                        </div>
                      </div>
                    );
                  })}
                  {sec.fields.length === 0 && (
                    <div onDragOver={e => e.preventDefault()} onDrop={() => onFieldDrop(sec.id, null)}
                         className="text-center py-6 text-xs text-slate-400 italic border-2 border-dashed border-slate-200 rounded-xl">
                      Glissez un champ ici, ou ajoutez-en un ci-dessous ↓
                    </div>
                  )}
                </div>

                {/* Add field bar */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {FIELD_TYPES.map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.value} onClick={() => addField(sec.id, t.value)} data-testid={`fb-add-${t.value}`}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-slate-200 hover:bg-blue-50 hover:border-blue-300 text-xs font-semibold text-slate-700">
                        <Icon className="w-3 h-3"/> {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <button onClick={addSection} data-testid="fb-add-section"
                    className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-500 hover:text-blue-700 text-sm font-bold flex items-center justify-center gap-2">
              <Plus className="w-4 h-4"/> Ajouter une section
            </button>
          </>
        )}
      </div>

      {/* Modals */}
      {editingField && (
        <FieldEditorModal
          field={editingField.field}
          formOptions={formOptions}
          onSave={(patch) => { updateField(editingField.sid, editingField.field.id, patch); setEditingField(null); }}
          onClose={() => setEditingField(null)}/>
      )}

      {modal === "new" && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setModal(null)}>
          <form onSubmit={createSchema} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-xl font-semibold">Nouveau formulaire</h2>
              <button type="button" onClick={() => setModal(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Titre</label>
                <input required value={newSchema.title} onChange={e => setNewSchema({...newSchema, title: e.target.value})} data-testid="fb-new-title"
                       className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder="Ex: Audit hygiène approfondi"/>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Page cible (clé)</label>
                <input required value={newSchema.page_key} onChange={e => setNewSchema({...newSchema, page_key: e.target.value})} data-testid="fb-new-page-key"
                       className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono" placeholder="ex: audit_hygiene"/>
                <div className="text-[11px] text-slate-500 mt-1">Uniquement lettres minuscules, chiffres et underscore.</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="fb-new-submit" className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white">Créer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FieldEditorModal({ field, formOptions, onSave, onClose }) {
  const [f, setF] = useState({ ...field });
  const isList = f.type === "list";
  const formKeys = Object.keys(formOptions).sort();

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-display text-xl font-semibold">Champ : {f.label}</h2>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Libellé</label>
            <input value={f.label} onChange={e => setF({...f, label: e.target.value})} data-testid="fb-fe-label"
                   className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Type</label>
            <SearchSelect value={f.type} onChange={v => setF({...f, type: v})} options={[...FIELD_TYPES.map(t => ({ value: t.value, label: t.label }))]} data-testid="fb-fe-type" size="lg" menuWidth={260}/>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Placeholder</label>
            <input value={f.placeholder || ""} onChange={e => setF({...f, placeholder: e.target.value})}
                   className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder="Texte indicatif"/>
          </div>
          {isList && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Utiliser une liste éditable</label>
              <SearchSelect value={f.form_options_key || ""} onChange={v => setF({...f, form_options_key: v || null})} options={[...formKeys.map(k => ({ value: k, label: `${k} (${(formOptions[k] || []).length} options)` }))]} placeholder={"— (options manuelles) —"} data-testid="fb-fe-list-source" size="lg" menuWidth={260}/>
              <div className="text-[11px] text-slate-500 mt-1">Les options sont administrables depuis « Configuration des Formulaires ».</div>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer p-2 rounded-xl border border-slate-200 hover:bg-slate-50">
            <input type="checkbox" checked={!!f.required} onChange={e => setF({...f, required: e.target.checked})} data-testid="fb-fe-required" className="w-4 h-4"/>
            <span className="text-sm text-slate-700 font-semibold">Champ obligatoire</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
          <button onClick={() => onSave(f)} data-testid="fb-fe-save" className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
